import { SPEAKER_COLORS, SpeakerColorEntry } from '@app/modules/diarization/speaker-utils';
import { Timestamp } from '@mi-front/core';
import { AppearanceSource, PersonAppearance, PersonLane } from './types';

/** Los segmentos son de 30 s; una cara continua produce un intervalo por segmento. */
export const MERGE_GAP_MS = 1000;

/** Prefijo de laneKey para voz sin person_id. */
export const VOICE_LANE_PREFIX = 'voice:';

function isBetterScore(source: AppearanceSource, candidate: number, current: number): boolean {
  return source === 'voice' ? candidate < current : candidate > current;
}

/**
 * Ordena por `from` y une intervalos contiguos de la misma fuente.
 * El score del merge es el mejor de los dos; el crop sigue al mejor score.
 */
export function mergeAppearances(items: PersonAppearance[]): PersonAppearance[] {
  const sorted = [...items].sort((a, b) => a.from - b.from);
  const merged: PersonAppearance[] = [];

  for (const item of sorted) {
    const prev = merged[merged.length - 1];
    if (prev && item.from - prev.to <= MERGE_GAP_MS) {
      const better = isBetterScore(item.source, item.score, prev.score);
      merged[merged.length - 1] = {
        ...prev,
        to: Math.max(prev.to, item.to) as Timestamp,
        score: better ? item.score : prev.score,
        crop: better && item.crop ? item.crop : prev.crop,
      };
      continue;
    }
    merged.push({ ...item });
  }

  return merged;
}

function totalMs(items: PersonAppearance[]): number {
  return items.reduce((acc, a) => acc + (a.to - a.from), 0);
}

/** Key con la que `SpeakerColorEntry` identifica a esta lane (person_id o voice_label formateado). */
export function laneColorKey(lane: Pick<PersonLane, 'laneKey' | 'personId'>): string {
  return lane.personId ?? lane.laneKey.slice(VOICE_LANE_PREFIX.length);
}

/**
 * Agrupa apariciones por laneKey, mergea intervalos por fuente, calcula totales,
 * asigna color y ordena por tiempo total desc.
 */
export function buildPersonLanes(
  appearances: PersonAppearance[],
  speakers: SpeakerColorEntry[] = [],
): PersonLane[] {
  const groups = new Map<string, PersonAppearance[]>();
  for (const a of appearances) {
    const list = groups.get(a.laneKey);
    if (list) {
      list.push(a);
    } else {
      groups.set(a.laneKey, [a]);
    }
  }

  const lanes: PersonLane[] = [];
  for (const [laneKey, items] of groups) {
    const first = items[0];
    const face = mergeAppearances(items.filter((a) => a.source === 'face'));
    const voice = mergeAppearances(items.filter((a) => a.source === 'voice'));
    const isUnknown = items.some((a) => a.isUnknown);

    // Un solo crop por lane para no retener MB de base64 en memoria.
    const bestFace = face.reduce<PersonAppearance | undefined>(
      (best, a) => (a.crop && (!best || a.score > best.score) ? a : best),
      undefined,
    );

    lanes.push({
      laneKey,
      personId: first.personId,
      fallbackLabel: items.find((a) => a.fallbackLabel)?.fallbackLabel ?? '',
      isUnknown,
      color: '',
      face: face.map((a) => ({ ...a, crop: undefined })),
      voice,
      faceTotalMs: totalMs(face),
      voiceTotalMs: totalMs(voice),
      appearances: face.length + voice.length,
      crop: isUnknown ? bestFace?.crop : undefined,
    });
  }

  lanes.sort((a, b) => {
    const diff = b.faceTotalMs + b.voiceTotalMs - (a.faceTotalMs + a.voiceTotalMs);
    return diff !== 0 ? diff : a.laneKey.localeCompare(b.laneKey);
  });

  // ponytail: offset de speakers.length para no pisar los índices que ya usó
  // buildSpeakerColorMap; con muchas lanes sin match puede repetir entre sí.
  return lanes.map((lane, index) => ({
    ...lane,
    color:
      speakers.find((s) => s.key === laneColorKey(lane))?.color ??
      SPEAKER_COLORS[(speakers.length + index) % SPEAKER_COLORS.length],
  }));
}
